import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaHeart,
  FaHandHoldingHeart,
  FaSeedling,
  FaUsers,
  FaMusic,
  FaMapMarkerAlt,
  FaHandshake,
  FaHandsHelping,
} from "react-icons/fa";
import "./CaringForTribalChildren.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const CaringForTribalChildren = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    {
      icon: FaUsers,
      title: "Community Participation",
      text: "We sensitize parents and use locally-made teaching materials to ensure deep community involvement in education.",
    },
    {
      icon: FaMusic,
      title: "Co-curricular Activities",
      text: "We facilitate cultural shows, games, sports, and debates to encourage the holistic growth of every child.",
    },
    {
      icon: FaMapMarkerAlt,
      title: "Exposure & Outing",
      text: "Tours to townships and scenic spots are organized to broaden children's minds and instill excitement.",
    },
    {
      icon: FaHandshake,
      title: "Advocacy & Networking",
      text: "We involve experts and build alliances at state and national levels to make our programs sustainable.",
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section (unchanged) */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Caring_for_Tribal_Children.png)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Caring for Tribal Children
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash is dedicated to caring for tribal children by providing access to education, healthcare, and support services to help them thrive in their communities.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* ================= Introduction ================= */}
      <section className="ctc-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <div className="ctc-eyebrow">
                  <span className="ctc-eyebrow__icon">
                    <FaSeedling />
                  </span>
                  <span className="ctc-eyebrow__dash" />
                </div>
                <h2 className="ctc-heading">
                  Joyful Learning
                  <br />
                  Through <span>Community</span>
                </h2>
                <p className="ctc-intro__text">
                  We believe joyful learning can only happen with community
                  participation. Parents are sensitized to their crucial role,
                  and we adopt a play-way teaching method using locally made
                  materials—often donated by parents—to involve the entire
                  community in educating children from vulnerable groups.
                </p>

                <div className="ctc-callout">
                  <span className="ctc-callout__icon">
                    <FaUsers />
                  </span>
                  <p className="ctc-callout__text">
                    Strong communities create bright futures. Together, we can
                    ensure every child learns, grows, and thrives.
                  </p>
                </div>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <div className="ctc-imageStack">
                  <span className="ctc-imageStack__dots" />
                  <span className="ctc-imageStack__circle" />
                  <img
                    src="/assets/images/Subsections/tcl1.jpg"
                    alt="Tribal children in a learning environment"
                    className="ctc-imageStack__img ctc-imageStack__img--back"
                  />
                  <img
                    src="/assets/images/Subsections/tcl2.jpg"
                    alt="Community participation in education"
                    className="ctc-imageStack__img ctc-imageStack__img--front"
                  />
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Key Initiatives ================= */}
      <section className="ctc-approach">
        <span className="ctc-approach__dots ctc-approach__dots--left" aria-hidden="true" />
        <span className="ctc-approach__dots ctc-approach__dots--right" aria-hidden="true" />
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="ctc-approach__header"
          >
            <span className="ctc-approach__badge">
              <FaHandsHelping />
            </span>
            <h2 className="ctc-heading ctc-heading--center">
              Our Approach to <span>Child Development</span>
            </h2>
            <p className="ctc-approach__lead">
              We focus on a holistic strategy that blends education with
              community engagement and advocacy.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item) => {
                const Icon = item.icon;
                return (
                  <Col md={6} key={item.title}>
                    <motion.div variants={staggerItem} className="ctc-card">
                      <span className="ctc-card__icon">
                        <Icon />
                      </span>
                      <div>
                        <h5 className="ctc-card__title">{item.title}</h5>
                        <p className="ctc-card__text">{item.text}</p>
                      </div>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="ctc-cta">
        <Container>
          <motion.div
            className="ctc-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="ctc-cta__imageWrap">
              <img
                src="/assets/images/Subsections/tcl1.jpg"
                alt="Tribal children"
                className="ctc-cta__image"
              />
              <span className="ctc-cta__badge">
                <FaHandHoldingHeart />
              </span>
            </div>

            <div className="ctc-cta__content">
              <span className="ctc-cta__icon">
                <FaHeart />
              </span>
              <h2 className="ctc-cta__title">
                Support a Tribal
                <br />
                <span>Child's Future</span>
              </h2>
              <span className="ctc-cta__underline" />
              <p className="ctc-cta__text">
                Your contribution provides vital education, healthcare, and
                nurturing support to help tribal children thrive.
              </p>
              <div className="ctc-cta__actions">
                <button
                  type="button"
                  className="ctc-btn ctc-btn--solid"
                  onClick={openDonation}
                >
                  <FaHeart /> Donate
                </button>
                <button
                  type="button"
                  className="ctc-btn ctc-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>

            <span className="ctc-cta__dots" aria-hidden="true" />
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default CaringForTribalChildren;